import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
//
import { InvoiceToolbar } from '.';
import { TableRowLink } from '@/components/table';
import { Iconify, Label, Image, Scrollbar } from '@/components';
// utils
import { fDate } from '@/utils/formatTime';
import { fCurrency } from '@/utils/formatNumber';
import { bookingsManagementPath } from '@/features/bookings-management';
//
import { InvoiceAdjustment, ProviderInvoice } from '../../types';
import { LabelColors } from '@/types';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { updateInvoice } from '../../api';
// ----------------------------------------------------------------------

const RowResultStyle = styled(TableRow)(({ theme }) => ({
  '& td': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  invoice: ProviderInvoice;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    url?: string;
    registration?: string;
  };
  };

  const PREDEFINED_ADJUSTMENTS: InvoiceAdjustment[] = [
    { description: 'DBS Check', amount: 0, type: 'credit' },
    { description: 'Overpayment deduction', amount: 0, type: 'credit' },
  { description: 'Consumables/Equipment charge', amount: 0, type: 'debit' },
];

export default function InvoiceDetails({ invoice, companyInfo }: Props) {
  const theme = useTheme();

  const {
    id,
    due_date,
    total,
    created_at,
    invoice_status,
    provider_invoice_items,
  } = invoice;

  const { successMsg, errorMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const [isAdjustmentDialogOpen, setIsAdjustmentDialogOpen] = useState(false);
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
  const [adjustmentType, setAdjustmentType] = useState<'debit' | 'credit'>(
    'debit'
  );

  const adjustments = useMemo(
    () => invoice.manual_adjustments || [],
    [invoice.manual_adjustments]
  );

  const adjustmentsTotal = useMemo(
    () =>
      adjustments.reduce((sum, adj) => {
        const amount = Number(adj.amount) || 0;
        const signed =
          adj.type === 'credit'
            ? -Math.abs(amount)
            : adj.type === 'debit'
            ? Math.abs(amount)
            : amount;
        return sum + signed;
      }, 0),
    [adjustments]
  );

  const baseTotal = useMemo(
    () => invoice.base_total ?? total ?? 0,
    [invoice.base_total, total]
  );

  const grandTotal = useMemo(
    () => invoice.adjusted_total ?? baseTotal + adjustmentsTotal,
    [invoice.adjusted_total, baseTotal, adjustmentsTotal]
  );

  const normalizeAddress = (address?: string) => {
    if (!address) return undefined;
    return address
      .replace(/<\s*\/?\s*br\s*\/?\s*>/gi, '\n')
      .replace(/\s+\n/g, '\n')
      .trim();
  };

  const resetAdjustmentForm = () => {
    setAdjustmentDescription('');
    setAdjustmentAmount('');
    setAdjustmentType('debit');
  };

  const handleCloseAdjustmentDialog = () => {
    setIsAdjustmentDialogOpen(false);
    resetAdjustmentForm();
  };

  const updateAdjustmentsMutation = useMutation({
    mutationFn: (nextAdjustments: InvoiceAdjustment[]) =>
      updateInvoice(invoice.id, { manual_adjustments: nextAdjustments }),
    onSuccess: () => {
      successMsg('Invoice adjustments updated');
      queryClient.invalidateQueries(['invoiceDetails', String(invoice.id)]);
      handleCloseAdjustmentDialog();
    },
    onError: (error) => {
      errorMsg(error, 'Failed to update invoice adjustments');
    },
  });

  const handleAddAdjustment = () => {
    const parsedAmount = Number(adjustmentAmount);
    if (
      !adjustmentDescription.trim() ||
      Number.isNaN(parsedAmount) ||
      parsedAmount === 0
    ) {
      errorMsg(
        new Error('Description and non-zero amount are required'),
        'Unable to add adjustment'
      );
      return;
    }

    const newAdjustment: InvoiceAdjustment = {
      description: adjustmentDescription.trim(),
      amount: Math.abs(parsedAmount),
      type: adjustmentType,
    };

    updateAdjustmentsMutation.mutate([...adjustments, newAdjustment]);
  };

  const handleRemoveAdjustment = (index: number) => {
    const nextAdjustments = adjustments.filter((_, idx) => idx !== index);
    updateAdjustmentsMutation.mutate(nextAdjustments);
  };

  const handleSelectPreset = (preset: InvoiceAdjustment) => {
    setAdjustmentDescription(preset.description);
    setAdjustmentType(preset.type || 'debit');
    setAdjustmentAmount(
      preset.amount && preset.amount !== 0 ? String(preset.amount) : ''
    );
    setIsAdjustmentDialogOpen(true);
  };

  const providerAddress =
    invoice.users?.address?.find((addr) => addr.default) ||
    invoice.users?.address?.[0];

  const providerName = `${invoice.users?.first_name ?? ''} ${
    invoice.users?.last_name ?? ''
  }`.trim();

  const providerContact = {
    name: providerName || 'Provider',
    address: normalizeAddress(providerAddress?.formatted_address),
    phone: invoice.users?.phone,
    email: invoice.users?.email,
  };

  return (
    <>
      <InvoiceToolbar invoice={invoice} companyInfo={companyInfo} />

      <Card sx={{ pt: 5, px: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Iconify icon="eva:plus-circle-outline" />}
            onClick={() => setIsAdjustmentDialogOpen(true)}
            disabled={updateAdjustmentsMutation.isLoading}
          >
            Add adjustment
          </Button>
        </Box>

        <Grid container>
          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Image
              disabledEffect
              visibleByDefault
              alt="logo"
              src="/logo/logo_full.svg"
              sx={{ maxWidth: 120 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Box sx={{ textAlign: { sm: 'right' } }}>
              <Label
                variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                color={
                  invoice_status
                    ? (invoice_status.style_class as LabelColors)
                    : 'default'
                }
                sx={{ textTransform: 'uppercase', mb: 1, fontSize: 18 }}
              >
                {invoice_status.value}
              </Label>

              <Typography variant="h6">{`INV-${id}`}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Typography
              paragraph
              variant="overline"
              sx={{ color: 'text.disabled' }}
            >
              Invoice from
            </Typography>
            <Typography variant="body2">
              {providerContact.name || 'Provider'}
            </Typography>
            {providerContact.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {providerContact.address}
              </Typography>
            )}
            {providerContact.phone && (
              <Typography variant="body2">
                Phone: {providerContact.phone}
              </Typography>
            )}
            {providerContact.email && (
              <Typography variant="body2">
                Email: {providerContact.email}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Typography
              paragraph
              variant="overline"
              sx={{ color: 'text.disabled' }}
            >
              Invoice to
            </Typography>
            <Typography variant="body2">
              {companyInfo?.name || 'Get Set Clean'}
            </Typography>
            {companyInfo?.address && (
              <Typography variant="body2">{companyInfo.address}</Typography>
            )}
            {companyInfo?.phone && (
              <Typography variant="body2">
                Phone: {companyInfo.phone}
              </Typography>
            )}
            {companyInfo?.email && (
              <Typography variant="body2">
                Email: {companyInfo.email}
              </Typography>
            )}
            {companyInfo?.url && (
              <Typography variant="body2">{companyInfo.url}</Typography>
            )}
            {companyInfo?.registration && (
              <Typography variant="body2">
                Company No: {companyInfo.registration}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Typography
              paragraph
              variant="overline"
              sx={{ color: 'text.disabled' }}
            >
              date create
            </Typography>
            <Typography variant="body2">{fDate(created_at)}</Typography>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Typography
              paragraph
              variant="overline"
              sx={{ color: 'text.disabled' }}
            >
              Due date
            </Typography>
            <Typography variant="body2">{fDate(due_date)}</Typography>
          </Grid>
        </Grid>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 960 }}>
            <Table>
              <TableHead
                sx={{
                  borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
                  '& th': { backgroundColor: 'transparent' },
                }}
              >
                <TableRow>
                  <TableCell width={40}>#</TableCell>
                  <TableCell align="left">Description</TableCell>
                  <TableCell align="left">Hours</TableCell>
                  <TableCell align="right">Price/h</TableCell>
                  <TableCell align="right">Cleaning Subtotal</TableCell>
                  <TableCell align="center" width="100">
                    Cleaning Products
                  </TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {provider_invoice_items.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      borderBottom: (theme) =>
                        `solid 1px ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell align="left">
                      {row.booking_sessions ? ( // on some occasions it is null
                        <Box sx={{ maxWidth: 560 }}>
                          <Typography variant="subtitle2">
                            <TableRowLink
                              linkText={
                                '#' + row.job_id + ':' + row.description
                              }
                              linkTo={bookingsManagementPath('view', {
                                bookingId: String(
                                  row.booking_sessions.booking.id
                                ),
                                bookingTabSid: 'booking-sessions',
                                sessionFilter: `previous?sessionID=${row.job_id}`,
                              })}
                            />
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', pl: 1 }}
                            noWrap
                          >
                            {row.users.first_name} {row.users.last_name} (
                            {row.booking_sessions.booking.address.postcode}) on{' '}
                            {row.job_timestamp}
                          </Typography>
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="left">{row.hours}</TableCell>
                    <TableCell align="right">
                      {fCurrency(row.price_per_hour)}
                    </TableCell>
                    <TableCell align="right">
                      {fCurrency(row.subtotal)}
                    </TableCell>
                    <TableCell align="center">
                      {row.cleaning_products}
                    </TableCell>
                    <TableCell align="right">{fCurrency(row.total)}</TableCell>
                  </TableRow>
                ))}

                <RowResultStyle>
                  <TableCell colSpan={5} />
                  <TableCell align="right">
                    <Box sx={{ mt: 2 }} />
                    <Typography>Subtotal</Typography>
                  </TableCell>
                  <TableCell align="right" width={120}>
                    <Box sx={{ mt: 2 }} />
                    <Typography>{fCurrency(baseTotal)}</Typography>
                  </TableCell>
                </RowResultStyle>

                {adjustments.length ? (
                  adjustments.map((adj, index) => {
                    const amount = Number(adj.amount) || 0;
                    const signedAmount =
                      adj.type === 'credit'
                        ? -Math.abs(amount)
                        : adj.type === 'debit'
                        ? Math.abs(amount)
                        : amount;
                    const isNegative = signedAmount < 0;
                    return (
                      <TableRow
                        key={`adjustment-${index}`}
                        sx={{
                          borderBottom: (theme) =>
                            `solid 1px ${theme.palette.divider}`,
                        }}
                      >
                        <TableCell>{`A${index + 1}`}</TableCell>
                        <TableCell align="left" colSpan={4}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">
                              {adj.description}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                textTransform: 'capitalize',
                              }}
                            >
                              {adj.type === 'credit'
                                ? 'Credit (deduction)'
                                : 'Debit (charge)'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="right" width={120}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <Typography
                              sx={{
                                color: isNegative
                                  ? 'error.main'
                                  : 'success.main',
                              }}
                            >
                              {fCurrency(signedAmount)}
                            </Typography>
                            <Tooltip title="Remove adjustment">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveAdjustment(index)}
                                disabled={updateAdjustmentsMutation.isLoading}
                              >
                                <Iconify
                                  icon="eva:trash-2-outline"
                                  width={18}
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow
                    sx={{
                      borderBottom: (theme) =>
                        `solid 1px ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell />
                    <TableCell align="left" colSpan={5}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        No adjustments added yet.
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width={120}>
                      —
                    </TableCell>
                  </TableRow>
                )}

                <RowResultStyle>
                  <TableCell colSpan={5} />
                  <TableCell align="right">
                    <Typography>Adjustments</Typography>
                  </TableCell>
                  <TableCell align="right" width={120}>
                    <Typography
                      sx={{
                        color:
                          adjustmentsTotal < 0 ? 'error.main' : 'success.main',
                      }}
                    >
                      {fCurrency(adjustmentsTotal)}
                    </Typography>
                  </TableCell>
                </RowResultStyle>

                <RowResultStyle>
                  <TableCell colSpan={5} />
                  <TableCell align="right">
                    <Typography variant="h6">Total</Typography>
                  </TableCell>
                  <TableCell align="right" width={140}>
                    <Typography variant="h6">
                      {fCurrency(grandTotal)}
                    </Typography>
                  </TableCell>
                </RowResultStyle>
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <Divider sx={{ mt: 5 }} />

        <Grid container>
          <Grid item xs={12} md={9} sx={{ py: 3 }}>
            <Typography variant="subtitle2">NOTES</Typography>
            <Typography variant="body2">
              We appreciate your business.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ py: 3, textAlign: 'right' }}>
            <Typography variant="subtitle2">Have a Question?</Typography>
            <Typography variant="body2">support@getsetclean.co.uk</Typography>
          </Grid>
        </Grid>
      </Card>

      <Dialog
        open={isAdjustmentDialogOpen}
        onClose={handleCloseAdjustmentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Quick add</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {PREDEFINED_ADJUSTMENTS.map((preset, idx) => (
                  <Chip
                    key={idx}
                    label={`${preset.description}${
                      preset.amount ? ` (${preset.amount})` : ''
                    }`}
                    onClick={() => handleSelectPreset(preset)}
                    icon={
                      <Iconify
                        icon={
                          (preset.type || 'debit') === 'credit'
                            ? 'fa6-solid:minus'
                            : 'fa6-solid:plus'
                        }
                      />
                    }
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>

            <TextField
              label="Description"
              value={adjustmentDescription}
              onChange={(event) => setAdjustmentDescription(event.target.value)}
              fullWidth
              autoFocus
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
            >
              <TextField
                label="Amount"
                type="number"
                value={adjustmentAmount}
                onChange={(event) => setAdjustmentAmount(event.target.value)}
                style={{ width: 150 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography>,
                }}
              />
              <TextField
                select
                label="Type (Credit = deduct from provider payment)"
                value={adjustmentType}
                onChange={(event) =>
                  setAdjustmentType(event.target.value as 'debit' | 'credit')
                }
                fullWidth
              >
                <MenuItem value="debit">Debit / Charge (+)</MenuItem>
                <MenuItem value="credit">Credit / Deduction (-)</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdjustmentDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAdjustment}
            disabled={updateAdjustmentsMutation.isLoading}
          >
            {updateAdjustmentsMutation.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
